import {
  MARKDOWN_HEADER_REGEX,
  MARKDOWN_UNDERLINE_HEADER_REGEX
} from '@commutifi-fe/constants'

/**
 * Extract markdown headers from a string.
 * @param content - The string to extract headers from.
 * @returns An array of objects with the header level and text.
 */
export function extractMarkdownHeaders(
  content: string,
  options: {
    /**
     * Whether to extract only the top level headers.
     */
    extractOnlyTopLevelHeaders?: boolean
  } = {}
) {
  // Extract headers with # style and underline style (=== or ---)
  const allHeaders: { level: number; text: string }[] = []

  // First, handle hash-style headers
  let match

  while ((match = MARKDOWN_HEADER_REGEX.exec(content)) !== null) {
    const level = match[1].length
    const text = match[2].trim()
    allHeaders.push({ level, text })
  }

  // Then, handle underline-style headers
  while ((match = MARKDOWN_UNDERLINE_HEADER_REGEX.exec(content)) !== null) {
    // === is level 1 (h1), --- is level 2 (h2)
    const level = match[2] === '=' ? 1 : 2
    const text = match[1].trim()
    allHeaders.push({ level, text })
  }

  if (options.extractOnlyTopLevelHeaders) {
    // Find the minimum header level (top level)
    const minLevel =
      allHeaders.length > 0 ? Math.min(...allHeaders.map((h) => h.level)) : 0

    // Filter to only include the top level headers
    return allHeaders.filter((h) => h.level === minLevel)
  }

  return allHeaders
}

/**
 * Cleans header text by removing markdown formatting
 * @param text The header text to clean
 * @returns The cleaned text without markdown formatting
 */
export const cleanMarkdownHeaderText = (text: string): string => {
  // Remove bold markdown (**text** or __text__)
  let cleanedText = text.replace(/(\*\*|__)(.*?)\1/g, '$2')

  // Remove italic markdown (*text* or _text_)
  cleanedText = cleanedText.replace(/(\*|_)(.*?)\1/g, '$2')

  // Remove code blocks (`text`)
  cleanedText = cleanedText.replace(/`(.*?)`/g, '$1')

  // Remove link text part from markdown links [text](url)
  cleanedText = cleanedText.replace(/\[(.*?)\]\(.*?\)/g, '$1')

  // Remove HTML tags
  cleanedText = cleanedText.replace(/<\/?[^>]+(>|$)/g, '')

  // Remove any extra whitespace
  cleanedText = cleanedText.trim()

  return cleanedText
}

export function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .replace(/ /g, '-')
}
